import { List as ImmutableList } from 'immutable';
import startCase from 'lodash/startCase';
import uniqBy from 'lodash/uniqBy';
import { DateTime, Interval } from 'luxon';

export const getTimestepFormatting = (timeStep) => {
  switch (timeStep) {
    case 'days':
      return 'day';
    case 'years':
      return 'year';
    default:
      return 'month';
  }
};
const getISOMask = (timeStamp, timeStep) => {
  switch (timeStep) {
    case 'days':
      return timeStamp.slice(0, 10);
    case 'years':
      return timeStamp.slice(0, 4);
    default:
      return timeStamp.slice(0, 7);
  }
};

export const constructDatePeriods = (dateRange, timeStep) => {
  if (!dateRange || !timeStep) return ImmutableList();

  const timeStepWithoutS = getTimestepFormatting(timeStep);

  let dateTicker = dateRange.start.endOf(timeStepWithoutS);
  const endDate = dateRange.end;

  const dateTimeIncrement = { [timeStep]: 1 };
  return ImmutableList().withMutations((boi) => {
    //  Retain the first dates real date
    const firstPeriodTimeStamp = dateTicker.toISO();
    const firstPeriod = {
      timeStamp: firstPeriodTimeStamp,
      total: null,
      isoMask: getISOMask(firstPeriodTimeStamp, timeStep),
    };
    boi.push(firstPeriod);
    dateTicker = dateTicker.plus(dateTimeIncrement);

    //  All subsequent dates in between will always be the END of their time period
    while (dateTicker.startOf(timeStepWithoutS) <= endDate.startOf(timeStepWithoutS)) {
      const timeStamp =
        dateTicker.plus(dateTimeIncrement).startOf(timeStepWithoutS) > endDate.startOf(timeStepWithoutS)
          ? endDate.toISO()
          : dateTicker.endOf(timeStepWithoutS).toISO(); // Retain the last dates real date
      // These fields get treated as account names if you don't filter them out in getSavingsReportForDisplay
      const period = {
        timeStamp,
        total: null,
        isoMask: getISOMask(timeStamp, timeStep),
      };
      boi.push(period);
      dateTicker = dateTicker.plus(dateTimeIncrement);
    }
  });
};

export const normalizePayee = (payee) => {
  if (!payee) return null;
  const withoutSymbols = payee.replace(/[^\w\s]/g, ''); // remove anything not alphanumeric or whitespace
  const withNormalizedWhiteSpace = withoutSymbols.replace(/\s+/g, ' '); // change groups of whitespace to a single one
  const lowerCasePayee = withNormalizedWhiteSpace.toLowerCase(); // set to lower case
  const startCasePayee = startCase(lowerCasePayee); // convert to startCase, capitalize first letter of each word
  return startCasePayee.trim(); // trim excess whitespace from the start and end
};

export const getGroupingKeys = (txn, split, groupBy, categoriesById, tagsById) => {
  switch (groupBy) {
    case 'categories':
    case 'category': {
      const txnItem = txn.split ? split : txn;
      const txnCat = txnItem.coa?.type === 'CATEGORY' ? categoriesById.get(txnItem.coa.id) : null;
      const parentCat = txnCat && txnCat.parentId !== '0' ? categoriesById.get(txnCat.parentId) : null;
      if (txnCat) {
        return {
          groupKeys: [parentCat ? parentCat.name : txnCat.name],
          subKey: txnCat.name,
          parentCategoryId: parentCat?.id || txnCat.id, // used for deep linking
          subCategoryId: txnCat.id, // used for deep linking
        };
      }
      return { groupKeys: ['Uncategorized'], subKey: null, parentCategoryId: '0' }; // not categorized
    }
    case 'payees':
    case 'payee': {
      return {
        groupKeys: [txn.payee || 'no payee'],
        subKey: null,
      };
    }
    case 'tags':
    case 'tag': {
      const tagGroupKeys = split.tags
        ?.map((tag) => tagsById.get(tag.id)?.name || null)
        .filter((val) => val !== null)
        ?.toJS?.();
      return {
        groupKeys: tagGroupKeys && tagGroupKeys.length > 0 ? tagGroupKeys : ['Untagged'],
        // groupKey: txn.tags?.reduce((accum, curr) => `${accum}${accum.length > 0 ? '/' : ''}${tagsById.get(curr.get('id')).get('name')}`, '') || 'Untagged',
        subKey: null,
      };
    }
    default: {
      return { groupKeys: ['total'], subKey: null };
    }
  }
};

// split might be the same as txn but we need the txn if split actually is a split, for payee
export const getGroupKey = (txn, split, groupBy, categoriesById, tagsById) => {
  if (groupBy === 'category') {
    if (txn.split) {
      return split.coa.type === 'CATEGORY' ? categoriesById.get(split.coa.id)?.name : 'Uncategorized';
    }
    return txn.coa.type === 'CATEGORY' ? categoriesById.get(txn.coa.id)?.name : 'Uncategorized';
  }

  if (groupBy === 'payee') {
    return txn.payee || 'no payee';
  }

  if (groupBy === 'tag') {
    return (
      split.tags?.reduce(
        (accum, curr) => `${accum}${accum.length > 0 ? '/' : ''}${tagsById.get(curr.id)?.get('name') || ''}`,
        '',
      ) || 'Untagged'
    );
  }
  return 'total';
};

// helpers for getReportSpecificFilterFromReportName, returning true means keep
const spendingFilter = (txn, categoriesById) => {
  if (txn.split?.items?.size > 0) {
    return txn.split.items.some((split) => spendingFilter(split, categoriesById));
  }
  if (!txn.coa && !txn.split?.items) {
    return false;
  }
  if (txn.coa?.type && txn.coa.type === 'UNCATEGORIZED') {
    return true;
  }
  const category = categoriesById.get(txn.coa && txn.coa.id);
  return category && category.type === 'EXPENSE';
};
const incomeFilter = (txn, categoriesById) => {
  if (txn.split?.items?.size > 0) {
    return txn.split.items.some((split) => incomeFilter(split, categoriesById));
  }
  if (!txn.coa) {
    return false;
  }
  const category = categoriesById.get(txn.coa.id);
  return category && category.type === 'INCOME';
};
const noUpcomingFilter = (txn) => txn.source !== 'SCHEDULED_TRANSACTION_PENDING';

const keepAll = () => true;

export function getReportSpecificFilterFromReportName(reportName) {
  switch (reportName) {
    case 'spending':
      return spendingFilter;
    case 'income':
      return incomeFilter;
    case 'net-worth':
    case 'savings':
      return noUpcomingFilter;
    default:
      return keepAll;
  }
}

// takes a grouping object from getTotalData and converts subgroups into sorted lists
export function convertAndSortGrouping(groupObject, sign) {
  return Object.entries(groupObject)
    .sort(([_a, a], [_b, b]) => sign * b.total - sign * a.total)
    .map(([_g, group]) => {
      const convertedSubgroup = convertAndSortGrouping(group.subgroups || {}, sign);
      return {
        ...group,
        subgroups: convertedSubgroup,
        total: sign * group.total,
      };
    });
}

// expects a list of groups with lists of subgroups and caps them to a specific length
export function capGrouping(uncappedGrouping, cap, overflowGroupName, filter) {
  const cappedGrouping = [];
  const txns = [];
  const txnItems = [];
  let total = 0;

  if (uncappedGrouping.length <= cap) {
    uncappedGrouping.forEach((group) => {
      if (!group.isIgnored && filter) {
        total += group.total;
        cappedGrouping.push(group);
      } else if (!filter) {
        total += group.total;
        cappedGrouping.push(group);
      }
      const txnsToPush = group.txns || [];
      txns.push(...txnsToPush);
      txnItems.push(...(group.txnItems || []));
    });
  } else {
    // here we need to do some work
    for (let i = 0; i < cap - 1; i++) {
      // first go through and add groups that aren't changing
      const group = uncappedGrouping[i];
      if (!group.isIgnored && filter) {
        total += group.total;
        cappedGrouping.push(group);
      } else if (!filter) {
        total += group.total;
        cappedGrouping.push(group);
      }
      const txnsToPush = group.txns || [];
      txns.push(...txnsToPush);
      txnItems.push(...(group.txnItems || []));
    }
    // add in the overflow group
    const [subGroup, subTxns, subTotal, subTxnItems] = capGrouping(
      uncappedGrouping.slice(cap - 1),
      cap,
      overflowGroupName,
      filter,
    ); //  Beautiful recursion
    total += subTotal;
    txns.push(...subTxns);
    txnItems.push(...subTxnItems);
    cappedGrouping.push({
      groupKey: overflowGroupName,
      total: subTotal,
      txns: subTxns,
      txnItems: subTxnItems,
      subgroups: subGroup,
    });
  }

  // when groups are created in the selector 'getTotalData' a txn can technically end up in multiple groups because
  //  of splits and multiple tags, this iterates over and removes the duplicates by id
  // TODO: do we need to adjust the total tho?
  const uniqueTxns = uniqBy(txns, (e) => e.id);

  return [cappedGrouping, uniqueTxns, total, txnItems];
}

export function adjustDateRangeEndToToday(dateRange) {
  if (dateRange.end.startOf('day').toISODate() === DateTime.local().startOf('day').toISODate()) {
    return dateRange;
  }
  return Interval.fromDateTimes(dateRange.start, DateTime.local().endOf('day'));
}

const groupByToPlural = {
  category: 'categories',
  payee: 'payees',
  tag: 'tags',
  none: '',
};

export function makeGroupByPlural(groupBy) {
  return groupByToPlural[groupBy] || '';
}
